import { Button, Center, useDisclosure, useToast, Text } from '@chakra-ui/react';
import { MdAutoAwesome } from 'react-icons/md';
import { ChatbotFormV4 } from '../components/ChatbotFormV4';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useChatbot, useChatbotLinks, useUpdateChatbot, useUpdateChatbotLinks } from '@/lib/sdk/chatbot/queries';
import { Task, TaskChanges, TaskProperties, WorkflowLinks } from '@/lib/sdk/chatbot/types';
import { ChatbotFormData } from '@/lib/sdk/chatbot/types';
import type { AIDraftExport } from '@/lib/sdk/ai-chat/types';
import { AIBuildPanel } from '../ai/AIBuildPanel';
import { changedTaskIds, formDataFromExport, linksDiffer } from '../ai/draftExport';
import { useCanBuildWithAI } from '../ai/useCanBuildWithAI';
import { transformTaskFromApiPayload } from '@/lib/sdk/chatbot/api';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { useState } from 'react';
import { UpdateDialog } from '@/components/Chatbot/UpdateDialog';
import { PageContainer } from '@/client/components/layout/PageContainer';

export const EditChatbotV4 = () => {
  const toast = useToast();
  const { orgId, chatbotId } = useParams();
  const navigate = useNavigate();
  const [isUpdating, setIsUpdating] = useState(false);
  const [updates, setUpdates] = useState<TaskChanges[]>([]);
  const [flowPublished, setFlowPublished] = useState(false);
  const updateChatbotMutation = useUpdateChatbot();
  const updateLinksMutation = useUpdateChatbotLinks();
  const { data: chatbot, isLoading, error } = useChatbot(chatbotId || '');
  const { data: linksData } = useChatbotLinks(chatbot?.short_code || '');
  const canBuildWithAI = useCanBuildWithAI();
  const [searchParams] = useSearchParams();
  const aiPanel = useDisclosure({ defaultIsOpen: searchParams.get('ai') === '1' });
  // A draft from the AI assistant: its form values, the tasks it changed, and new
  // links, which are saved with the workflow (the edit request carries no links)
  const [loadedDraft, setLoadedDraft] = useState<{ key: number; data: ChatbotFormData; changed: Set<string>; links?: WorkflowLinks } | null>(null);

  const openDraft = (draftExport: AIDraftExport) => {
    if (!draftExport.update || !chatbot) return;
    const links = linksDiffer(linksData?.links, draftExport.links) ? draftExport.links ?? {} : undefined;
    setLoadedDraft({
      key: Date.now(),
      data: formDataFromExport(draftExport.update),
      changed: changedTaskIds(chatbot.task_list, draftExport.update.tasks),
      links,
    });
    toast({ title: 'Draft opened in the editor', description: 'Changed tasks are highlighted. Review them, then save.', status: 'success', duration: 4000 });
  };

  const breadcrumbItems = [
    { label: 'Dashboard', href: `/dashboard/${orgId}/workflows` },
    { label: 'Edit Chatbot' },
  ];

  const handleSubmit = async (data: ChatbotFormData) => {
    try {
      setIsUpdating(true);
      const response = await updateChatbotMutation.mutateAsync({
        id: chatbotId || '',
        mode: 'ui',
        data: {
          title: data.title,
          template: data.template,
          ttl: data.ttl ?? 1,
          tasks: data.tasks,
          tags: data.tags,
          task_lifecycle: data.task_lifecycle,
          ttl_unit: data.ttl_unit,
        }
      });
      if (loadedDraft?.links && chatbot?.short_code) {
        await updateLinksMutation.mutateAsync({ code: chatbot.short_code, links: loadedDraft.links });
      }

      if (response.changes) {
        setUpdates(response.changes);
        setFlowPublished(Boolean(response.flows_enabled && response.flow_published));
      } else {
        handleUpdateComplete();
      }

      toast({
        title: 'Chatbot updated successfully',
        status: 'success',
        duration: 3000,
      });
    } catch (error: any) {
      toast({
        title: 'Error updating chatbot',
        description: error.message,
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUpdateComplete = () => {
    setUpdates([]);
    navigate(`/dashboard/${orgId}/workflows`);
  };

  const formatTasks = (tasks: TaskProperties[]): Task[] => {
    return tasks.map(transformTaskFromApiPayload);
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <Center minH="100vh" bg="gray.50">
        <Text color="red.500">Error loading chatbot: {error.message}</Text>
      </Center>
    );
  }

  return (
    <PageContainer
      title="Workflows"
      description="Edit an existing workflow configuration."
      breadcrumbs={breadcrumbItems}
    >
      <ChatbotFormV4
        key={loadedDraft?.key ?? 'saved'}
        onSubmit={handleSubmit}
        isLoading={isUpdating}
        chatbotCode={chatbot?.short_code}
        orgId={orgId}
        flowsEnabled={chatbot?.flows_enabled}
        highlightTaskIds={loadedDraft?.changed}
        taskToolbarActions={
          canBuildWithAI ? (
            <Button
              size="sm"
              leftIcon={<MdAutoAwesome />}
              variant="solid"
              colorScheme="brand"
              onClick={aiPanel.onOpen}
              data-testid="wfv4-ai-btn"
            >
              Edit with AI
            </Button>
          ) : undefined
        }
        initialData={loadedDraft?.data ?? {
          title: chatbot?.title || '',
          template: chatbot?.template || '',
          ttl: chatbot?.ttl || 24,
          task_lifecycle: chatbot?.task_lifecycle || 'daily',
          ttl_unit: chatbot?.ttl_unit || 'hours',
          tasks: formatTasks(chatbot?.task_list || []),
          tags: chatbot?.tags || [],
        }}
      />

      {canBuildWithAI && chatbot && (
        <AIBuildPanel isOpen={aiPanel.isOpen} onClose={aiPanel.onClose} targetChatbotId={chatbot.id} onOpenInEditor={openDraft} />
      )}

      <UpdateDialog
        isOpen={updates.length > 0}
        onClose={handleUpdateComplete}
        changes={updates || []}
        flowPublished={flowPublished}
      />
    </PageContainer>
  );
};

export default EditChatbotV4;
